<template>
  <div class="container mx-auto mt-16 px-4 pb-20">
    <h1 class="font-bold text-4xl mb-8">
      {{ isNew ? "Ny kategori" : "Redigera kategori" }}
    </h1>

    <FormErrors :errors="errors" subject="category" />

    <form @submit.prevent="submit">
      <div class="flex flex-col mb-20 gap-5">
        <div class="mt-8">
          <Label>Namn</Label>
          <Input v-model="form.category.name" type="text" class="mt-2" />
        </div>

        <div>
          <Label>Slug (URL)</Label>
          <Input
            v-model="form.category.slug"
            type="text"
            class="mt-2"
            placeholder="t.ex. bakverk"
          />
          <p class="text-xs text-muted-foreground mt-1">
            Lämna tomt för att auto-generera från namnet.
          </p>
        </div>

        <div>
          <Label>Undertitel</Label>
          <Input
            v-model="form.category.subtitle"
            type="text"
            class="mt-2"
            placeholder="e.g. Från surdeg till saftiga bullar"
          />
        </div>

        <div>
          <Label>Överordnad kategori</Label>
          <select
            v-model="form.category.parent_id"
            class="mt-2 flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-base shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
          >
            <option value="">Ingen</option>
            <option
              v-for="parent in parentCategories"
              :key="parent.id"
              :value="parent.id"
            >
              {{ parent.name }}
            </option>
          </select>
        </div>

        <div>
          <Label>Färg på kategori-etiketten</Label>
          <p class="text-sm text-muted-foreground mt-1 mb-3">
            Välj en färg — den visas på receptkorten som tillhör den här
            kategorin.
          </p>
          <div class="flex flex-wrap gap-3 mt-2">
            <button
              v-for="color in colorPalette"
              :key="color.value"
              type="button"
              class="w-9 h-9 rounded-full border-2 transition-all"
              :style="{ backgroundColor: color.value }"
              :class="
                form.category.color === color.value
                  ? 'border-[#191c1e] scale-110'
                  : 'border-transparent'
              "
              :title="color.label"
              @click="form.category.color = color.value"
            />
            <button
              v-if="form.category.color"
              type="button"
              class="w-9 h-9 rounded-full border-2 border-dashed border-gray-300 flex items-center justify-center text-gray-400 text-xs hover:border-gray-400 transition-all"
              title="Ta bort färg"
              @click="form.category.color = ''"
            >
              ✕
            </button>
          </div>
        </div>

        <div>
          <Label>Omslagsbild</Label>
          <div v-if="category.cover_image_url" class="mt-2 mb-3">
            <img
              :src="category.cover_image_url"
              alt="Nuvarande omslagsbild"
              class="h-40 w-auto rounded-lg object-cover"
            />
            <p class="text-sm text-muted-foreground mt-1">
              Nuvarande bild — ladda upp en ny för att ersätta den
            </p>
          </div>
          <Input
            type="file"
            accept="image/*"
            class="mt-2"
            @change="onFileChange"
          />
        </div>
      </div>

      <div class="mt-8 flex gap-4">
        <Button type="submit" :disabled="form.processing">
          {{ isNew ? "Skapa kategori" : "Spara ändringar" }}
        </Button>
        <Button as="a" :href="categoriesPath" variant="outline">
          Tillbaka till kategorier
        </Button>
      </div>
    </form>

    <div
      v-if="!isNew"
      class="mt-12 rounded-2xl border border-[#f1d2d2] bg-[#fff8f8] p-6"
    >
      <h2 class="font-[Poppins] font-bold text-xl text-[#191c1e] mb-2">
        Ta bort kategori
      </h2>
      <p class="text-sm text-[#6c7a77] mb-4">
        Den här kategorin innehåller {{ category.recipes_count || 0 }} recept.
      </p>

      <div
        v-if="category.children_count > 0"
        class="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900"
      >
        <p>
          Kategorin har {{ category.children_count }} underkategorier. Flytta
          eller ta bort underkategorierna först innan den här kategorin kan tas
          bort.
        </p>
        <div class="mt-3 flex flex-wrap gap-2">
          <Link
            v-for="child in category.children || []"
            :key="child.id"
            :href="child.edit_path"
            class="rounded-full border border-amber-300 bg-white px-3 py-1 text-xs font-semibold text-amber-900 hover:bg-amber-100 transition-colors"
          >
            {{ child.name }}
          </Link>
        </div>
      </div>

      <div v-else class="flex flex-col gap-4 max-w-[440px]">
        <div v-if="(category.recipes_count || 0) > 0">
          <Label>Flytta recept till annan kategori</Label>
          <select
            v-model="replacementCategoryId"
            class="mt-2 flex h-10 w-full rounded-md border border-input bg-white px-3 py-2 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
          >
            <option value="">Välj kategori</option>
            <option
              v-for="replacement in replacementCategories"
              :key="replacement.id"
              :value="replacement.id"
            >
              {{ replacement.name }}
            </option>
          </select>
          <p class="mt-2 text-sm text-[#6c7a77]">
            Recepten flyttas till vald kategori innan den här kategorin tas
            bort.
          </p>
        </div>

        <div class="flex items-center gap-3">
          <Button type="button" variant="destructive" @click="destroyCategory">
            Ta bort kategori
          </Button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, ref } from "vue";
import { Link, router, useForm } from "@inertiajs/vue3";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import FormErrors from "@/components/form/FormErrors.vue";

const props = defineProps({
  category: { type: Object, required: true },
  parentCategories: { type: Array, default: () => [] },
  replacementCategories: { type: Array, default: () => [] },
  currentUser: { type: Object, default: null },
  actionPath: { type: String, required: true },
  categoriesPath: { type: String, required: true },
  categoryPath: { type: String, default: "" },
  errors: { type: Array, default: () => [] },
});

const isNew = computed(() => !props.category.id);
const replacementCategoryId = ref("");

const form = useForm({
  category: {
    name: props.category.name ?? "",
    slug: props.category.slug ?? "",
    subtitle: props.category.subtitle ?? "",
    parent_id: props.category.parent_id ?? "",
    color: props.category.color ?? "",
    cover_image: null,
  },
});

const colorPalette = [
  { label: "Teal", value: "#14b8a6" },
  { label: "Bärnsten", value: "#f59e0b" },
  { label: "Violett", value: "#8b5cf6" },
  { label: "Rosa", value: "#ec4899" },
  { label: "Blå", value: "#3b82f6" },
  { label: "Röd", value: "#ef4444" },
  { label: "Grön", value: "#10b981" },
  { label: "Orange", value: "#f97316" },
];

function onFileChange(e) {
  form.category.cover_image = e.target.files[0] ?? null;
}

function submit() {
  const options = {};
  if (form.category.cover_image) {
    options.forceFormData = true;
  }

  form.transform((data) => {
    const category = {
      ...data.category,
      subtitle: data.category.subtitle === "" ? null : data.category.subtitle,
      parent_id:
        data.category.parent_id === "" ? null : data.category.parent_id,
    };
    if (!category.cover_image) {
      delete category.cover_image;
    }
    return { category };
  });

  if (isNew.value) {
    form.post(props.actionPath, options);
  } else {
    form.patch(props.actionPath, options);
  }
}

function destroyCategory() {
  const recipeCount = Number(props.category.recipes_count || 0);

  if (props.category.children_count > 0) {
    window.alert("Ta först bort eller flytta underkategorierna.");
    return;
  }

  if (recipeCount > 0 && !replacementCategoryId.value) {
    window.alert("Välj först vilken kategori recepten ska flyttas till.");
    return;
  }

  const confirmText =
    recipeCount > 0
      ? `Ta bort ${props.category.name} och flytta ${recipeCount} recept till vald kategori?`
      : `Ta bort ${props.category.name}?`;

  if (!window.confirm(confirmText)) return;

  const data =
    recipeCount > 0
      ? { replacement_category_id: replacementCategoryId.value }
      : {};

  router.delete(props.category.delete_path, {
    data,
  });
}
</script>
